import { useState } from 'react';
import { isS3UrlValid } from '../../pages/HistoricalSearch/ForensicSearchUtil';

const useDownload = (videoId, activeBlobUrl, mediaId, onError, onFetchFreshUrl) => {
  const [isLoading, setIsLoading] = useState(false);

  const triggerBlobDownload = (blob) => {
    const blobUrl = URL.createObjectURL(blob);
    triggerDownload(blobUrl, videoId);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  };

  // Attempt to download from presigned URL. isFreshAttempt=false: first try, rethrow on fail.
  // isFreshAttempt=true: retry with fresh URL, show error if still fails.
  const performDownload = async (url, isFreshAttempt = false) => {
    try {
      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        cache: 'force-cache',
        headers: {}
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const blob = await response.blob();
      triggerBlobDownload(blob);
    } catch (e) {
      if (!isFreshAttempt) {
        throw e; // First attempt failed; let caller fetch fresh URL and retry
      }
      console.error("S3 download failed with fresh URL:", e);
      onError?.("Video download failed. Please try again.");
    }
  };

  const downloadViaS3Url = async () => {
    try {
      if (!isS3UrlValid(activeBlobUrl)) {
        console.warn("S3 URL expired, attempting to fetch fresh URL");

        if (onFetchFreshUrl) {
          try {
            const freshUrl = await onFetchFreshUrl();
            if (!freshUrl) {
              console.error("Failed to fetch fresh S3 URL");
              onError?.("Unable to prepare video for download. Please try again.");
              setIsLoading(false);
              return;
            }

            if (!isS3UrlValid(freshUrl)) {
              console.error("Fresh S3 URL is invalid");
              onError?.("Unable to prepare video for download. Please try again.");
              setIsLoading(false);
              return;
            }

            console.log("Retrying download with fresh S3 URL");
            await performDownload(freshUrl, true);
            return;
          } catch (e) {
            console.error("Error fetching fresh URL:", e);
            onError?.("Unable to prepare video for download. Please try again.");
            setIsLoading(false);
            return;
          }
        } else {
          onError?.("Video link expired. Please try again.");
          setIsLoading(false);
          return;
        }
      }

      await performDownload(activeBlobUrl, false);
    } catch (e) {
      console.error("S3 download failed:", e);
      onError?.("Video download failed due to some unexpected error. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = (e) => {
    if (isLoading) return;
    setIsLoading(true);
    downloadViaS3Url();
  };

  const triggerDownload = (url, id) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = `video_${id || mediaId}.mp4`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return { isLoading, handleDownload };
};

export default useDownload;
